import { useCallback, useState } from 'react';
import * as referrals from '../services/referrals';
import { ApiError } from '../services/types';
import useAsync, { Async } from './useAsync';

export type UseReferrals = {
  summary: Async<referrals.ReferralSummary>;
  sent: Async<referrals.Referral[]>;
  /**
   * The referral this customer is the REFEREE of, or null when they signed
   * up without a code.
   *
   * Its own call rather than a field on the summary: the summary is about
   * codes this customer has given out, and this is the one they were given.
   * Somebody can be both, and reading one off the other would make a screen
   * that shows a friend's pending reward as though it were their own.
   */
  received: Async<referrals.Referral | null>;
  /** True while `apply()` is in flight. */
  applying: boolean;
  /**
   * Why the last `apply()` failed, already written for a human.
   *
   * The backend's own message is preferred when it sends one - it knows
   * whether the code was unknown, self-referred or already used, and says so
   * more precisely than the app can guess from a status code alone.
   */
  applyError: string | null;
  /** Wipes {@link applyError} - for a field being retyped after a refusal. */
  clearApplyError: () => void;
  /** Redeem a friend's code. Resolves true when it stuck. */
  apply: (code: string) => Promise<boolean>;
  reload: () => void;
};

/**
 * `signedIn` holds every request until there is an account to ask about.
 *
 * The Refer & Earn page is open to guests and stays mounted through sign-in.
 * Fetched once on mount, a guest's requests failed and nothing asked again -
 * the page kept its guest view after signing in until it was left and
 * reopened. Gated on this instead, the calls fire the moment it turns true.
 */
export function useReferrals(signedIn = true): UseReferrals {
  const gate = { enabled: signedIn };
  const summary = useAsync(() => referrals.summary(), [], gate);
  const sent = useAsync(() => referrals.sent(), [], gate);
  const received = useAsync(() => referrals.received(), [], gate);

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const apply = useCallback(
    async (code: string): Promise<boolean> => {
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) {
        setApplyError('Enter a code first.');
        return false;
      }

      setApplying(true);
      setApplyError(null);
      try {
        await referrals.apply(trimmed);
        // Both views move: the summary counts it, the list shows it.
        summary.reload();
        sent.reload();
        // And the one the applier themselves is now in - this is the call
        // that turns the entry form into "your code is applied".
        received.reload();
        return true;
      } catch (thrown) {
        setApplyError(messageFor(thrown));
        return false;
      } finally {
        setApplying(false);
      }
    },
    [summary, sent, received],
  );

  const reload = useCallback(() => {
    summary.reload();
    sent.reload();
    received.reload();
  }, [summary, sent, received]);

  const clearApplyError = useCallback(() => setApplyError(null), []);

  return {
    summary,
    sent,
    received,
    applying,
    applyError,
    clearApplyError,
    apply,
    reload,
  };
}

/**
 * A referral failure, as a sentence.
 *
 * The server's message wins when there is one. The fallbacks below only cover
 * the case where a status arrived with no body - a proxy timing out, say -
 * because inventing copy for a 409 the backend already explained would risk
 * contradicting it.
 */
function messageFor(thrown: unknown): string {
  if (thrown instanceof ApiError) {
    if (thrown.message) {
      return thrown.message;
    }
    if (thrown.status === 404) {
      return "That code doesn't exist. Check it and try again.";
    }
    if (thrown.status === 409) {
      return 'That code cannot be used on this account.';
    }
    if (thrown.status === 0) {
      return 'Check your connection and try again.';
    }
  }
  return 'Something went wrong. Try again.';
}

export default useReferrals;
